import { FloatingLoader, Typography } from "@/components";
import { BackAppBar } from "@/components/ui";
import { QUERY_KEYS } from "@/libraries/constant";
import { getCasePatientVisits, getCaseQueryOptions } from "@/queries";
import { DataIdSchema } from "@/schemas";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import {
	onlineManager,
	useSuspenseInfiniteQuery,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import dayjs from "dayjs";
import { flatMap, groupBy } from "lodash";
import { useEffect, useMemo, useRef } from "react";

export const Route = createFileRoute(
	"/dashboard/case/view/$id/patient-visits/",
)({
	params: {
		parse: (params) => ({
			id: DataIdSchema.parse(Number(params.id)),
		}),
		stringify: ({ id }) => ({ id: id.toString() }),
	},
	loader: ({ context, params }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return [
				context.queryClient.ensureQueryData(getCaseQueryOptions(params.id)),
				context.queryClient.ensureInfiniteQueryData(
					getCasePatientVisits({ caseId: params.id }),
				),
			];
		}
		return [
			context.queryClient.getQueryData([QUERY_KEYS.GET_CASE, params.id]),
			context.queryClient.getQueryData([
				QUERY_KEYS.GET_CASE_PATIENT_VISITS,
				params.id,
			]),
		];
	},
	component: RouteComponent,
});

function RouteComponent() {
	const caseId = Route.useParams({
		select: (s) => s.id,
	});
	const caseQuery = useSuspenseQuery(getCaseQueryOptions(caseId));
	const patientVisitsQuery = useSuspenseInfiniteQuery(
		getCasePatientVisits({ caseId }),
	);
	const scrollRef = useRef<HTMLDivElement>(null);
	const title = useMemo(() => {
		if (!caseQuery.data.patient) return caseQuery.data.title;
		return [caseQuery.data.patient.name, caseQuery.data.patient.surname].join(
			" ",
		);
	}, [caseQuery.data]);

	const isClosed = caseQuery.data.status === "closed";

	const patientVisits = useMemo(() => {
		const visits = patientVisitsQuery.data ?? [];
		if (!visits.length) return [];
		const grouped = groupBy(visits, "date");
		return flatMap(grouped, (items, date) => {
			return [
				{ type: "date" as const, value: dayjs(date).format("DD MMM YYYY") },
				...items.map((item) => ({ type: "item" as const, value: item })),
			];
		});
	}, [patientVisitsQuery.data]);

	const patientVisitVirtualizer = useVirtualizer({
		count: patientVisits.length,
		gap: 12,
		overscan: 5,
		estimateSize: () => 202,
		getScrollElement: () => scrollRef.current,
		measureElement: (el) => el.getBoundingClientRect().height,
	});
	const patientVisitVirtualizerItems =
		patientVisitVirtualizer.getVirtualItems();

	useEffect(() => {
		const [lastItem] = [...patientVisitVirtualizerItems].reverse();

		if (!lastItem) return;

		if (
			lastItem.index >= patientVisits.length - 1 &&
			patientVisitsQuery.hasNextPage &&
			!patientVisitsQuery.isFetchingNextPage
		) {
			patientVisitsQuery.fetchNextPage();
		}
	}, [
		patientVisitsQuery.hasNextPage,
		patientVisitsQuery.fetchNextPage,
		patientVisits.length,
		patientVisitsQuery.isFetchingNextPage,
		patientVisitVirtualizerItems,
	]);

	return (
		<>
			<BackAppBar
				className="!bg-gradient-account [&_*_]:text-white"
				title={title}
				subTitle="Patient's Visits"
			/>
			<div ref={scrollRef} className="relative flex-1 pb-4 overflow-y-auto">
				<FloatingLoader
					open={
						patientVisitsQuery.isLoading ||
						patientVisitsQuery.isFetchingNextPage
					}
					className="top-5"
				/>
				<div
					className="relative w-full"
					style={{
						height: patientVisitVirtualizer.getTotalSize(),
					}}
				>
					{patientVisitVirtualizerItems.map((patientVisitVirtualizerItem) => {
						const patientVisit =
							patientVisits[patientVisitVirtualizerItem.index];
						if (patientVisit.type === "date") {
							return (
								<Divider
									key={patientVisitVirtualizerItem.key}
									ref={patientVisitVirtualizer.measureElement}
									data-index={patientVisitVirtualizerItem.index}
								>
									<Typography fontWeight={500} fontSize={12} className="py-2">
										{patientVisit.value}
									</Typography>
								</Divider>
							);
						}
						return (
							<div
								key={patientVisitVirtualizerItem.key}
								ref={patientVisitVirtualizer.measureElement}
								data-index={patientVisitVirtualizerItem.index}
								className="container"
							>
								<div className="flex flex-col gap-1 bg-white p-4 rounded-xl">
									<div className="flex flex-row justify-between items-center">
										<Typography fontWeight={400} fontSize={14}>
											Date
										</Typography>
										<Typography fontWeight={500} fontSize={14}>
											{patientVisit.value.date}
										</Typography>
									</div>
									<div className="flex flex-row justify-between items-center">
										<Typography fontWeight={400} fontSize={14}>
											Time
										</Typography>
										<Typography fontWeight={500} fontSize={14}>
											{patientVisit.value.time}
										</Typography>
									</div>
									<div className="flex flex-row justify-between items-center">
										<Typography fontWeight={400} fontSize={14}>
											Location
										</Typography>
										<Typography fontWeight={500} fontSize={14}>
											{patientVisit.value.location}
										</Typography>
									</div>
									<div className="flex flex-row justify-between items-center">
										<Typography fontWeight={400} fontSize={14}>
											Department
										</Typography>
										<Typography fontWeight={500} fontSize={14}>
											{patientVisit.value.department}
										</Typography>
									</div>
									<div className="flex flex-row justify-between items-center">
										<Typography fontWeight={400} fontSize={14}>
											Seen by
										</Typography>
										<Typography fontWeight={500} fontSize={14}>
											{patientVisit.value.seen_by}
										</Typography>
									</div>
									<Divider />
									<div className="flex flex-col justify-center">
										<Typography fontWeight={700} fontSize={14}>
											{patientVisit.value.user.fullname}
										</Typography>
										<Typography fontWeight={400} fontSize={12}>
											Submitted at:{" "}
											<Typography
												component="span"
												fontWeight={500}
												fontSize={12}
											>
												{dayjs(patientVisit.value.created_at).format(
													"DD MMM YYYY, HH:mm A",
												)}
											</Typography>
										</Typography>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>
			{!isClosed ? (
				<div className="bg-white py-4">
					<div className="container">
						<Link
							to="/dashboard/case/view/$id/patient-visits/create"
							params={{ id: caseId }}
						>
							<Button
								fullWidth
								variant="contained"
								color="primary"
								size="large"
								className="!rounded-full"
								startIcon={<AddIcon />}
							>
								Add Visit
							</Button>
						</Link>
					</div>
				</div>
			) : null}
		</>
	);
}
